import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  captureAborted,
  clearPending,
  getPending,
  normalizeScreen,
  setPending,
} from "@/lib/routeTiming";

/**
 * Mounts once inside <BrowserRouter> and stamps a pending screen-load record
 * on every route change to an instrumented screen, plus fires
 * `screen_load_aborted` when the user leaves before the screen becomes ready.
 *
 * Abort semantics — brief-background overcount, accepted:
 *
 *   If the user tabs away for a moment while /browse is still loading and
 *   then returns, we will emit `screen_load_aborted { exit_type: "background" }`
 *   and NO subsequent `screen_load_completed` for that navigation (the pending
 *   record is cleared on `visibilitychange -> hidden`). This overcounts "true"
 *   abandonment because the user did come back. Accepted because
 *     (a) they genuinely paused the load,
 *     (b) tracking "resumed" would require re-arming pending on `visible` and
 *         reconciling duplicate completes,
 *     (c) iOS WKWebView doesn't reliably fire `visible` after a long background.
 *   Consumers should treat `exit_type: "background"` as "load interrupted",
 *   not "user gone forever".
 *
 *   iOS force-quits may deliver neither `visibilitychange` nor `pagehide`.
 *   Accepted limitation per WKWebView behavior.
 */
export function AppRouteTiming(): null {
  const location = useLocation();
  const isFirstNavRef = useRef(true);

  // Route-change handler
  useEffect(() => {
    const now = performance.now();
    const pending = getPending();

    const nextScreen = normalizeScreen(location.pathname);

    // Leaving a pending screen (either to another tracked screen or to an
    // untracked route) counts as a navigation abort.
    if (pending && pending.screen !== nextScreen) {
      captureAborted(pending, now, "navigation");
      clearPending();
    }

    if (nextScreen && (!pending || pending.screen !== nextScreen)) {
      const initial = isFirstNavRef.current;
      setPending({
        screen: nextScreen,
        // For the very first navigation of the session, measure from
        // performance.timeOrigin so the reported duration includes the full
        // cold-start cost (bundle download, parse, hydrate).
        startedAt: initial ? 0 : now,
        initial,
        cacheHitCandidate: false,
      });
    }

    isFirstNavRef.current = false;
  }, [location.pathname]);

  // Visibility / pagehide listeners — fire the abort capture synchronously.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") return;
      const pending = getPending();
      if (!pending) return;
      captureAborted(pending, performance.now(), "background");
      clearPending();
    };

    const onPageHide = () => {
      const pending = getPending();
      if (!pending) return;
      captureAborted(pending, performance.now(), "pagehide");
      clearPending();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  return null;
}

export default AppRouteTiming;
